import { useLoading } from '@gorila-shared-ui/components';
import { OnChangeParams } from 'baseui/select';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useRecoilState, useResetRecoilState } from 'recoil';
import { useFeedback } from '../../../hooks/useFeedback';
import { attendEvent } from '../../../services/gptService';
import { attendEventFormState, attendingEventState } from '../../../storage/gptStates';
import { AttendEventRequest } from '../../../types/gptTypes';
import { useGptEventDetail } from './useGptEventDetail';

export function useGptAttendEvent() {
  const { eventId } = useParams();
  const { startLoading, stopLoading, loading } = useLoading(false);
  const { showFailFeedback, showPositiveFeedback } = useFeedback();
  const { event } = useGptEventDetail();
  const [isAttending, setIsAttending] = useRecoilState(attendingEventState);
  const [form, setForm] = useRecoilState(attendEventFormState);
  const resetForm = useResetRecoilState(attendEventFormState);

  const onInputChange = (value: string, field: keyof AttendEventRequest) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const onSave = async () => {
    if (!eventId || !form.code.length) return;
    startLoading();
    const { error } = await attendEvent(eventId, form);
    if (!error) {
      showPositiveFeedback('Se asignó el motivo para atender el evento.');
      closeAttend();
    } else {
      showFailFeedback(error ?? 'No se pudo atender el evento, inténtalo nuevamente.');
    }
    stopLoading();
  };

  const reasonOptions = useMemo(() => {
    if (!event?.sgptEventOptions) return;
    return event.sgptEventOptions.map((eventType) => ({
      id: eventType.code,
      label: eventType.name,
    }));
  }, [event]);

  const onReasonChange = (params: OnChangeParams) => {
    params.option?.id ? onInputChange(params.option.id as string, 'code') : onInputChange('', 'code');
  };

  const onAttend = () => {
    setIsAttending(true);
  };

  const closeAttend = () => {
    setIsAttending(false);
    resetForm();
  };

  const formIsValid = !!form.code.length;

  return {
    closeAttend,
    eventId,
    form,
    formIsValid,
    isAttending,
    loading,
    onAttend,
    onInputChange,
    onReasonChange,
    onSave,
    reasonOptions,
  };
}
